import { useEffect, useRef, useState, useContext } from 'react';
import { AuthContext } from '../../context/auth-context';
import TradesAPI from '../../../services/api/admin/trades-api';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ShopSyncAPI from '../../../services/api/integrations/shop-sync-api';
import ProjectsAPI from '../../../services/api/admin/projects-api';
import ACCAPI from '../../../services/api/integrations/acc-api';
import Logger from '../../../utils/logger';
import ProjectWorkflowEditorModal from './ProjectWorkflowEditorModal';
import LinkAccProjectDialog from './LinkAccProjectDialog';

const ACC_SYNC_POLL_INTERVAL_MS = 5000;
const ACC_SYNC_POLL_TIMEOUT_MS = 15 * 60 * 1000;

// Shown when linking's automatic ACC asset setup does not finish. There is
// no retry control afterward — the admin re-links or asks for help.
const ACC_ASSET_SETUP_FAILED_MESSAGE =
  'ACC asset setup failed. Try re-linking, or contact support or a developer.';

// Why the backend did not start the ACC sync after linking.
const AUTO_SYNC_NOT_STARTED_MESSAGES = {
  sync_already_running:
    'Another ACC sync is running, so ACC asset setup did not start. Try re-linking once it finishes, or contact support or a developer.',
  not_syncable:
    'This project is not active, so ACC asset setup did not start. Make it active and try re-linking, or contact support or a developer.',
};
const AUTO_SYNC_NOT_STARTED_DEFAULT = ACC_ASSET_SETUP_FAILED_MESSAGE;

export const ProjectModal = ({
  project,
  editProject,
  open,
  handleCancel,
  handleAddProject,
  handleUpdateProject,
  handleProjectFieldPersisted,
  shopExceptionCount = 0,
  onViewExceptions,
  refreshExceptionCounts,
}) => {
  const auth = useContext(AuthContext);
  const [trades, setTrades] = useState([]);
  const [backfill, setBackfill] = useState({
    loading: false,
    result: null,
    error: null,
  });
  // Whether shop_sync_enabled is currently persisted as `true` in the DB.
  // The backfill only runs for saved-enabled projects, so this gates the button.
  const [shopSyncSaved, setShopSyncSaved] = useState(false);
  const [shopSyncSaving, setShopSyncSaving] = useState(false);
  const [workflowEditorOpen, setWorkflowEditorOpen] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  // The ACC sync the backend starts right after a successful link:
  // { projectId, phase: 'running' | 'success' | 'failed' | 'not_started' |
  //   'timeout' | 'interrupted', message }. Tied to the linked project because
  // the modal stays mounted and may be showing another project by the time
  // the sync finishes.
  const [accAutoSync, setAccAutoSync] = useState(null);
  const [currentProject, setCurrentProject] = useState({
    id: '',
    name: '',
    number: '',
    default_trade_id: '',
    active: true,
  });
  // Latest currentProject for the sync poll, which outlives renders.
  const currentProjectRef = useRef(currentProject);
  currentProjectRef.current = currentProject;

  useEffect(() => {
    if (project) {
      setCurrentProject({
        ...project,
        default_trade_id: project.default_trade_id || '',
      });
      setShopSyncSaved(!!project.shop_sync_enabled);
      Logger.debug('Project modal initialized', { project });
    }
  }, [project, editProject]);

  useEffect(() => {
    const fetchTrades = async () => {
      try {
        const tradesData = await TradesAPI.getAll(auth.token);
        setTrades(tradesData);
      } catch (err) {
        Logger.error('Error fetching trades', err);
        setTrades([]);
      }
    };
    if (open) {
      fetchTrades();
    }
  }, [open, auth.token]);

  const isLinkedToAcc =
    !!currentProject.autodesk_project_id &&
    String(currentProject.autodesk_project_id).trim() !== '';

  const isAccLinkLocked = isLinkedToAcc && !!currentProject.acc_last_sync_at;

  const handleLinkAccClick = () => {
    Logger.userAction('Opening ACC link dialog', {
      projectId: currentProject.id,
      number: currentProject.number,
      isRelink: isLinkedToAcc,
    });
    setLinkDialogOpen(true);
  };

  const handleAccLinked = (linkedProject, autoSync) => {
    const syncStarted = autoSync?.started === true;
    const updatedProject = {
      ...currentProject,
      autodesk_project_id: linkedProject.autodesk_project_id,
      // The backend marks the project in_progress as the sync starts, which
      // also sets acc_last_sync_at and locks relinking. Mirror that here so
      // the modal and grid match without a reload.
      ...(syncStarted && {
        acc_last_sync_at: new Date().toISOString(),
        acc_last_sync_status: 'in_progress',
        acc_last_sync_error: null,
      }),
    };
    setCurrentProject(updatedProject);
    if (handleProjectFieldPersisted) {
      handleProjectFieldPersisted(updatedProject);
    }

    setAccAutoSync(
      syncStarted
        ? { projectId: updatedProject.id, phase: 'running', stage: 'sync' }
        : {
            projectId: updatedProject.id,
            phase: 'not_started',
            message:
              AUTO_SYNC_NOT_STARTED_MESSAGES[autoSync?.reason] ||
              AUTO_SYNC_NOT_STARTED_DEFAULT,
          }
    );
  };

  // A finished auto-sync message is only useful until the modal is reopened.
  // A running one stays, so reopening the project mid-sync still shows it.
  useEffect(() => {
    if (open) {
      setAccAutoSync((prev) =>
        prev && prev.phase !== 'running' ? null : prev
      );
    }
  }, [open]);

  // The poll outlives renders, so it reads the latest callback through a ref.
  const handleProjectFieldPersistedRef = useRef(handleProjectFieldPersisted);
  handleProjectFieldPersistedRef.current = handleProjectFieldPersisted;

  const autoSyncProjectId =
    accAutoSync?.phase === 'running' ? accAutoSync.projectId : null;

  // Follow the ACC sync started by linking until it finishes. Keeps going if
  // the modal is closed (it stays mounted), so the grid still gets the result.
  useEffect(() => {
    if (!autoSyncProjectId || !auth.token) {
      return;
    }

    const projectId = autoSyncProjectId;
    const startedAt = Date.now();
    let cancelled = false;
    let timeoutId;

    // Write the sync outcome onto the project: into the modal if it is still
    // showing this project (keeping any unsaved edits), and into the grid.
    const publishResult = (syncFields) => {
      const shown = currentProjectRef.current;
      const persist = handleProjectFieldPersistedRef.current;
      if (shown.id === projectId) {
        const merged = { ...shown, ...syncFields };
        setCurrentProject(merged);
        if (persist) persist(merged);
      } else if (persist) {
        persist({ id: projectId, ...syncFields });
      }
    };

    const checkAgain = () => {
      if (cancelled) return;

      if (Date.now() - startedAt > ACC_SYNC_POLL_TIMEOUT_MS) {
        Logger.warn('Gave up waiting on ACC sync after linking', {
          projectId,
        });
        setAccAutoSync({
          projectId,
          phase: 'timeout',
          message:
            'The ACC setup is taking longer than expected. Reopen this project later. If it still has not finished, try re-linking, or contact support or a developer.',
        });
        return;
      }

      timeoutId = setTimeout(poll, ACC_SYNC_POLL_INTERVAL_MS);
    };

    const poll = async () => {
      if (cancelled) return;

      try {
        const status = await ACCAPI.getProjectSyncStatus(projectId, auth.token);
        if (cancelled) return;

        const syncFields = {
          acc_last_sync_at: status.lastSyncAt,
          acc_last_sync_status: status.lastSyncStatus,
          acc_last_sync_error: status.lastSyncError,
        };
        const setup = status.linkSetup;

        // The backend's record of the link setup: sync, then status set and
        // custom fields in ACC, then asset sync switched on.
        if (setup) {
          if (setup.state === 'syncing' || setup.state === 'setting_up') {
            const stage = setup.state === 'setting_up' ? 'setup' : 'sync';
            setAccAutoSync((prev) =>
              prev?.projectId === projectId &&
              prev.phase === 'running' &&
              prev.stage !== stage
                ? { ...prev, stage }
                : prev
            );
            checkAgain();
            return;
          }

          const done = setup.state === 'done';
          publishResult({
            ...syncFields,
            ...(done && { acc_asset_sync_enabled: true }),
          });

          if (done) {
            setAccAutoSync({
              projectId,
              phase: 'success',
              message:
                'ACC is set up: the project was synced, its status set and custom fields were created in ACC, and ACC asset sync is on.',
            });
          } else if (setup.stage === 'sync') {
            setAccAutoSync({
              projectId,
              phase: 'failed',
              message: `ACC sync failed${
                setup.error ? `: ${setup.error}` : '.'
              } ${ACC_ASSET_SETUP_FAILED_MESSAGE}`,
            });
          } else {
            setAccAutoSync({
              projectId,
              phase: 'failed',
              message: setup.error
                ? `ACC asset setup failed: ${setup.error}. Try re-linking, or contact support or a developer.`
                : ACC_ASSET_SETUP_FAILED_MESSAGE,
            });
          }
          return;
        }

        // No record of the setup: the server restarted since linking. Fall
        // back to the project's sync columns. isSyncRunning is the app-wide
        // lock, so also require this project to still be in_progress.
        if (status.isSyncRunning && status.lastSyncStatus === 'in_progress') {
          checkAgain();
          return;
        }

        publishResult(syncFields);

        if (status.lastSyncStatus === 'success') {
          setAccAutoSync({
            projectId,
            phase: 'interrupted',
            message:
              'ACC sync finished, but ACC asset setup failed (the server restarted). Try re-linking, or contact support or a developer.',
          });
        } else if (status.lastSyncStatus === 'failed') {
          setAccAutoSync({
            projectId,
            phase: 'failed',
            message: `ACC sync failed${
              status.lastSyncError ? `: ${status.lastSyncError}` : '.'
            } Fix the problem, then run Packages → Sync Assets for this project.`,
          });
        } else {
          // Still in_progress with no sync running: the server restarted
          // mid-sync, so it will never finish on its own.
          setAccAutoSync({
            projectId,
            phase: 'interrupted',
            message:
              'The ACC sync stopped before finishing. Run Packages → Sync Assets for this project.',
          });
        }
      } catch (err) {
        // Transient failures shouldn't abandon the wait — log and try again
        // until the timeout above gives up.
        Logger.error('Error checking ACC sync status after linking', err, {
          projectId,
        });
        checkAgain();
      }
    };

    timeoutId = setTimeout(poll, ACC_SYNC_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [autoSyncProjectId, auth.token]);

  const shownAutoSync =
    accAutoSync && accAutoSync.projectId === currentProject.id
      ? accAutoSync
      : null;
  const autoSyncRunning = shownAutoSync?.phase === 'running';

  const handleChange = (e) => {
    const itemChanged = e.target.name;

    let valueChanged;

    if (
      itemChanged === 'active' ||
      itemChanged === 'timecards' ||
      itemChanged === 'acc_schedule_enabled' ||
      itemChanged === 'shop_sync_enabled'
    ) {
      valueChanged = e.target.checked;
    } else if (itemChanged === 'kpi_earned_over_actual') {
      // Always store a real boolean; the API rejects the strings "true"/"false".
      valueChanged = e.target.value === true || e.target.value === 'true';
    } else {
      valueChanged = e.target.value;
    }

    const changeProject = { ...currentProject, [itemChanged]: valueChanged };
    setCurrentProject(changeProject);
  };

  // Shop sync is persisted the moment it's toggled (not on "Update Project"),
  // so the backfill button below can be used right away without reopening the
  // modal. The backend only runs backfills for saved-enabled projects.
  const handleShopSyncToggle = async (e) => {
    const nextEnabled = e.target.checked;
    const updatedProject = {
      ...currentProject,
      shop_sync_enabled: nextEnabled,
    };

    // Optimistic UI: reflect the switch immediately, then persist.
    setCurrentProject(updatedProject);
    setShopSyncSaving(true);
    try {
      Logger.userAction('Persisting shop_sync_enabled toggle', {
        projectId: updatedProject.id,
        shop_sync_enabled: nextEnabled,
      });
      await ProjectsAPI.update(updatedProject, auth.token);
      setShopSyncSaved(nextEnabled);
      // Keep the parent grid in sync without closing the modal.
      if (handleProjectFieldPersisted) {
        handleProjectFieldPersisted(updatedProject);
      }
    } catch (err) {
      Logger.error('Failed to persist shop_sync_enabled toggle', err);
      // Revert the switch on failure.
      setCurrentProject((p) => ({ ...p, shop_sync_enabled: !nextEnabled }));
      alert(err.response?.data?.message || err.message || 'Failed to save');
    } finally {
      setShopSyncSaving(false);
    }
  };

  // Shared runner for both the date-range and full-history backfills.
  const runBackfillRange = async (dateStart, dateEnd) => {
    setBackfill((b) => ({ ...b, loading: true, result: null, error: null }));
    try {
      Logger.userAction('Running shop time backfill', {
        projectId: currentProject.id,
        start: dateStart,
        end: dateEnd,
      });
      const result = await ShopSyncAPI.run(auth.token, {
        project_id: currentProject.id,
        date_start: dateStart,
        date_end: dateEnd,
        dry_run: false,
      });
      setBackfill((b) => ({ ...b, loading: false, result }));
      if (refreshExceptionCounts) await refreshExceptionCounts();
    } catch (err) {
      Logger.error('Shop time backfill failed', err);
      setBackfill((b) => ({
        ...b,
        loading: false,
        error: err.response?.data?.message || err.message || 'Backfill failed',
      }));
    }
  };

  const handleFullBackfill = () => {
    // "Full" = everything through today. A very early start captures all history;
    // the sync only reads timecards that actually exist.
    const today = new Date().toISOString().slice(0, 10);
    const ok = window.confirm(
      `Run a FULL historical backfill for "${currentProject.name}" — ALL shop time through ${today}?\n\n` +
        `This writes every shop timecard into Fieldtime and locks past weeks. ` +
        `It is idempotent (safe to re-run).`
    );
    if (ok) runBackfillRange('2000-01-01', today);
  };

  const handleClose = () => {
    setCurrentProject({
      id: '',
      name: '',
      number: '',
      default_trade_id: '',
      active: true,
    });

    handleCancel();
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    editProject
      ? handleUpdateProject(currentProject)
      : handleAddProject(currentProject);

    setCurrentProject({
      id: '',
      name: '',
      number: '',
      default_trade_id: '',
      active: true,
    });
  };

  return (
    <div>
      <Dialog open={open}>
        <DialogTitle>
          {editProject ? 'Edit Project' : 'New Project'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              required
              autoFocus
              fullWidth
              margin="normal"
              id="number"
              label="Project Number"
              name="number"
              autoComplete="off"
              type="text"
              value={currentProject.number}
              onChange={handleChange}
            />
            <TextField
              required
              fullWidth
              margin="normal"
              id="name"
              label="Project Name"
              name="name"
              autoComplete="off"
              type="text"
              value={currentProject.name}
              onChange={handleChange}
            />
            <TextField
              required
              select
              fullWidth
              margin="normal"
              id="default_trade_id"
              label="Default Trade"
              name="default_trade_id"
              value={currentProject.default_trade_id || ''}
              onChange={handleChange}
            >
              {(trades || []).map((trade) => (
                <MenuItem key={trade.id} value={trade.id}>
                  {trade.name}
                </MenuItem>
              ))}
            </TextField>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: -1, mb: 1, display: 'block' }}
            >
              This trade will be pre-selected when creating packages for this
              project
            </Typography>
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                color: 'secondary',
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    onChange={handleChange}
                    checked={currentProject.active}
                  />
                }
                label="Active"
                labelPlacement="start"
                id="active"
                name="active"
              />
            </Box>
            {editProject && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  ACC Sync
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1,
                    mb: 1,
                  }}
                >
                  {isLinkedToAcc ? (
                    <Tooltip title={currentProject.autodesk_project_id}>
                      <Chip
                        size="small"
                        color="success"
                        variant="outlined"
                        label="Linked to ACC"
                        icon={
                          isAccLinkLocked ? <LockOutlinedIcon /> : undefined
                        }
                      />
                    </Tooltip>
                  ) : (
                    <Chip size="small" label="Not Linked" />
                  )}

                  {isAccLinkLocked ? (
                    <Typography variant="caption" color="text.secondary">
                      Already synced — contact an admin to change this.
                    </Typography>
                  ) : (
                    <Button
                      type="button"
                      size="small"
                      variant="outlined"
                      onClick={handleLinkAccClick}
                    >
                      {isLinkedToAcc ? 'Re-link ACC' : 'Link ACC'}
                    </Button>
                  )}
                </Box>
                {autoSyncRunning && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <CircularProgress size={14} />
                    <Typography variant="caption" color="text.secondary">
                      {shownAutoSync.stage === 'setup'
                        ? 'Step 2 of 2: creating the status set and custom fields in ACC… this can take a minute.'
                        : 'Step 1 of 2: syncing from ACC… this can take a few minutes. The status set and custom fields are created next.'}
                    </Typography>
                  </Box>
                )}
                {shownAutoSync && !autoSyncRunning && (
                  <Alert
                    severity={
                      shownAutoSync.phase === 'success' ? 'success' : 'warning'
                    }
                    sx={{ mb: 1 }}
                    onClose={() => setAccAutoSync(null)}
                  >
                    {shownAutoSync.message}
                  </Alert>
                )}
                {isLinkedToAcc && (
                  <>
                    {!(
                      autoSyncRunning && !currentProject.acc_asset_sync_enabled
                    ) && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: 1,
                        }}
                      >
                        <Typography variant="body2">ACC asset sync:</Typography>
                        {currentProject.acc_asset_sync_enabled ? (
                          <Chip
                            size="small"
                            color="success"
                            variant="outlined"
                            label="On"
                          />
                        ) : (
                          <Chip size="small" label="Off" />
                        )}
                      </Box>
                    )}
                    {!currentProject.acc_asset_sync_enabled &&
                      !autoSyncRunning && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ mt: 0.5 }}
                        >
                          This is a legacy project created before ACC asset sync
                          was introduced. Set up packages directly in ACC and
                          sync them back to Fieldtime.
                        </Typography>
                      )}
                    {currentProject.acc_asset_sync_enabled && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ mt: 0.5 }}
                      >
                        The status set and custom fields are set up in ACC for
                        this project. Choose which packages go to ACC with each
                        package&apos;s &quot;Sync to ACC&quot; option.
                      </Typography>
                    )}
                    <FormControlLabel
                      sx={{ mt: 1 }}
                      control={
                        <Switch
                          name="acc_schedule_enabled"
                          checked={currentProject.acc_schedule_enabled || false}
                          onChange={handleChange}
                        />
                      }
                      label="Enable schedule sync for this project"
                    />
                    {currentProject.acc_schedule_enabled && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ mt: 0.5 }}
                      >
                        When enabled, this project will be included in ACC
                        schedule sync operations. The project must have an
                        active schedule in Autodesk Construction Cloud.
                      </Typography>
                    )}
                  </>
                )}

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Shop Time Sync (FabShopTools)
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      name="shop_sync_enabled"
                      checked={currentProject.shop_sync_enabled || false}
                      onChange={handleShopSyncToggle}
                      disabled={shopSyncSaving}
                    />
                  }
                  label="Enable nightly shop time sync for this project"
                />
                {currentProject.shop_sync_enabled && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mt: 0.5 }}
                  >
                    When enabled, shop timecards from FabShopTools are mirrored
                    into this project nightly (matched to the legacy project by
                    number). Anything that can't be mapped is logged as an
                    exception rather than written.
                  </Typography>
                )}

                {currentProject.shop_sync_enabled && (
                  <Box sx={{ mt: 1.5 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mb: 1 }}
                    >
                      Historical backfill — one-time catch-up of ALL shop time
                      for this project (the nightly job only covers the last 14
                      days).
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Button
                        type="button"
                        variant="outlined"
                        size="small"
                        color="secondary"
                        onClick={handleFullBackfill}
                        disabled={
                          backfill.loading || shopSyncSaving || !shopSyncSaved
                        }
                        startIcon={
                          backfill.loading ? (
                            <CircularProgress size={16} />
                          ) : null
                        }
                      >
                        {backfill.loading ? 'Running…' : 'Run Full Backfill'}
                      </Button>
                      {shopExceptionCount > 0 && (
                        <Tooltip title="Unresolved exceptions — click to view">
                          <Chip
                            size="small"
                            color="warning"
                            label={`${shopExceptionCount} exception${
                              shopExceptionCount === 1 ? '' : 's'
                            }`}
                            onClick={onViewExceptions}
                            sx={{ cursor: 'pointer' }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                    {shopSyncSaving && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ mt: 0.5 }}
                      >
                        Saving…
                      </Typography>
                    )}
                    {backfill.error && (
                      <Typography
                        variant="caption"
                        color="error"
                        display="block"
                        sx={{ mt: 0.5 }}
                      >
                        {backfill.error}
                      </Typography>
                    )}
                    {backfill.result && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ mt: 0.5 }}
                      >
                        Backfill done: {backfill.result.rows_inserted} inserted,{' '}
                        {backfill.result.rows_updated} updated,{' '}
                        {backfill.result.exceptions_found} exceptions,{' '}
                        reconciled={String(backfill.result.reconciled)}.
                      </Typography>
                    )}
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  KPI Formula Orientation
                </Typography>
                <TextField
                  select
                  fullWidth
                  margin="normal"
                  id="kpi_earned_over_actual"
                  label="LPF / SPI Formula"
                  name="kpi_earned_over_actual"
                  value={currentProject.kpi_earned_over_actual ?? false}
                  onChange={handleChange}
                >
                  <MenuItem value={false}>
                    Factor basis (Actual ÷ Earned) - under 1.0 = good (original
                    setting)
                  </MenuItem>
                  <MenuItem value={true}>
                    Index basis (Earned ÷ Actual) - over 1.0 = good (new
                    customer standard)
                  </MenuItem>
                </TextField>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: -1, mb: 1, display: 'block' }}
                >
                  Controls how LPF and SPI are calculated and colored on the
                  KPIs and KPI Trends tabs for this project. Changes what every
                  user sees.
                </Typography>

                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
                  <Button
                    type="button"
                    variant="outlined"
                    color="primary"
                    onClick={() => setWorkflowEditorOpen(true)}
                    sx={{ width: '100%', py: 1 }}
                  >
                    Edit Workflow
                  </Button>
                </Box>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button type="submit" variant="contained" sx={{ width: 180 }}>
              {editProject ? 'Update Project' : 'Add Project'}
            </Button>
            <Button
              variant="outlined"
              onClick={handleClose}
              sx={{ width: '25%' }}
            >
              Cancel
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      <ProjectWorkflowEditorModal
        open={workflowEditorOpen}
        handleClose={() => setWorkflowEditorOpen(false)}
        projectId={currentProject.id}
        projectName={currentProject.name}
        projectNumber={currentProject.number}
      />
      <LinkAccProjectDialog
        open={linkDialogOpen}
        project={currentProject}
        onClose={() => setLinkDialogOpen(false)}
        onLinked={handleAccLinked}
      />
    </div>
  );
};
